import React, { useEffect, useRef } from 'react';
import Hls from 'hls.js';
import { useMapStore } from '../store/useMapStore';
import { Play, Pause, X, RadioReceiver } from 'lucide-react';

export const AudioPlayer: React.FC = () => {
  const { 
    selectedRadioStation, 
    setSelectedRadioStation,
    isRadioPlaying, 
    setIsRadioPlaying 
  } = useMapStore();
  const audioRef = useRef<HTMLAudioElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  useEffect(() => {
    if (!selectedRadioStation || !audioRef.current) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      setIsRadioPlaying(false);
      return;
    }

    const streamUrl = `https://air.pc.cdn.bitgravity.com/air/live/pbaudio${selectedRadioStation.properties.streamId}/playlist.m3u8`;

    if (Hls.isSupported()) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      const hls = new Hls();
      hlsRef.current = hls;
      hls.loadSource(streamUrl);
      hls.attachMedia(audioRef.current);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        audioRef.current?.play().then(() => setIsRadioPlaying(true)).catch(e => console.error('Auto-play failed:', e));
      });
    } else if (audioRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      // Native Safari support
      audioRef.current.src = streamUrl;
      audioRef.current.addEventListener('loadedmetadata', () => {
        audioRef.current?.play().then(() => setIsRadioPlaying(true)).catch(e => console.error('Auto-play failed:', e));
      });
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [selectedRadioStation, setIsRadioPlaying]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isRadioPlaying) {
        audioRef.current.pause();
        setIsRadioPlaying(false);
      } else {
        audioRef.current.play();
        setIsRadioPlaying(true);
      }
    }
  };

  const closePlayer = () => {
    setSelectedRadioStation(null);
  };

  if (!selectedRadioStation) return null;

  return (
    <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-[1000] bg-white rounded-2xl shadow-xl border border-gray-100 flex items-center p-3 gap-4 w-[90vw] max-w-sm">
      <div className="bg-primary/10 p-3 rounded-full flex-shrink-0">
        <RadioReceiver className="w-6 h-6 text-primary" />
      </div>
      
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-gray-800 text-sm truncate">
          {selectedRadioStation.properties.name}
        </h4>
        <p className="text-xs text-gray-500 truncate">
          Akashvani {selectedRadioStation.properties.city} Live
        </p>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={togglePlay}
          className="p-2 bg-primary text-white rounded-full hover:bg-primary/90 transition-colors"
          aria-label={isRadioPlaying ? 'Pause' : 'Play'}
        >
          {isRadioPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>
        <button
          onClick={closePlayer}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Close player"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <audio ref={audioRef} className="hidden" />
    </div>
  );
};
